import { Edit3, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';

interface ProcessStep {
  stepNumber: number;
  title: string;
  subtitle: string;
  order: number;
}

interface ProcessMarketingEditorProps {
  steps: ProcessStep[];
  onChange: (steps: ProcessStep[]) => void;
}

export function ProcessMarketingEditor({ steps, onChange }: ProcessMarketingEditorProps) {
  const addStep = () => {
    onChange([
      ...steps,
      {
        stepNumber: steps.length + 1,
        title: 'Nouvelle étape',
        subtitle: 'Explication du fonctionnement',
        order: steps.length + 1,
      },
    ]);
  };

  const updateStep = (idx: number, patch: Partial<ProcessStep>) => {
    const next = [...steps];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const removeStep = (idx: number) => {
    onChange(steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepNumber: i + 1, order: i + 1 })));
  };

  return (
    <div className="relative pl-8 space-y-8 before:absolute before:left-[19px] before:top-4 before:bottom-4 before:w-0.5 before:bg-gray-100">
      {steps
        .sort((a, b) => a.order - b.order)
        .map((step, idx) => (
          <div key={idx} className="relative group">
            {/* Dot on timeline */}
            <div className="absolute -left-[37px] top-1 w-10 h-10 rounded-full bg-white border-4 border-gray-50 text-[#00758d] flex items-center justify-center font-black text-sm shadow-sm z-10 group-hover:border-[#00758d]/20 transition-all">
              {step.stepNumber}
            </div>

            <div className="bg-white border-2 border-gray-50 rounded-2xl p-5 shadow-sm group-hover:border-[#00758d]/20 transition-all relative">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Edit3 size={10} className="text-gray-300" />
                    <Label className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                      Action principale
                    </Label>
                  </div>
                  <Input
                    value={step.title}
                    onChange={(e) => updateStep(idx, { title: e.target.value })}
                    className="h-auto py-0 text-base font-black text-[#00303c] border-none focus-visible:ring-0 shadow-none bg-transparent"
                    placeholder="Ex: Choisissez votre formule"
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Label className="text-[9px] font-black uppercase tracking-widest text-gray-400">Précisions</Label>
                  </div>
                  <Input
                    value={step.subtitle}
                    onChange={(e) => updateStep(idx, { subtitle: e.target.value })}
                    className="h-auto py-0 text-sm font-medium text-gray-500 border-none focus-visible:ring-0 shadow-none bg-transparent"
                    placeholder="Ex: Demi-journée, journée ou pack"
                  />
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeStep(idx)}
                className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white shadow-md border border-gray-100 text-red-400 opacity-0 group-hover:opacity-100 transition-all"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addStep}
        className="ml-2 text-[#00758d] font-bold text-xs hover:bg-[#00758d]/5 border-none h-10 rounded-xl"
      >
        <Plus size={16} className="mr-2" /> Ajouter une étape au processus
      </Button>
    </div>
  );
}
